/**
 * PROTOTYPE – texts not approved
 *
 * Geschichtenschritt round 3 — small pure helpers for the erfunden
 * step-3 field prefill, name-join rule, and dynamic headings.
 *
 *   joinNames(['Emil'])              → 'Emil'
 *   joinNames(['Emil', 'Bruno'])     → 'Emil und Bruno'
 *   joinNames(['Emil', 'Papa', 'Bruno']) → 'Emil, Papa und Bruno'
 *
 *   erfundenSentenceForTile('feuerwehr', 3 names)
 *     → the approved per-tile Feuerwehr sentence with the three
 *       joined names (Sebastian 2026-10-09 10:15 wording).
 *   erfundenSentenceForTile('eigene', ...)         → ''   (empty field)
 *
 *   erfundenFieldHeading('feuerwehr')   → "Euer Feuerwehr-Abenteuer"
 *   erfundenFieldHeading('eigene')      → "Eure eigene Idee"
 *
 *   adventureQuestionForCast([])                       → "Welches Abenteuer wollt ihr erleben?"
 *   adventureQuestionForCast([Emil])                   → "Welches Abenteuer will Emil erleben?"
 *   adventureQuestionForCast([Emil, Papa, Bruno])      → "Welches Abenteuer wollen Emil, Papa und Bruno erleben?"
 *   adventureQuestionForCast(4+ named entries)         → "Welches Abenteuer wollt ihr erleben?"
 */

import { STRINGS } from '@/strings/strings';
import type { ErfundenTileSlug } from '../../state/book-configurator-store';

export interface ErfundenCastEntry {
  readonly name: string;
}

/** Themes carried by every non-eigene tile — used for both the sentence
 *  template and the field heading. `eigene` has no theme. */
export const TILE_THEMES: Readonly<
  Record<Exclude<ErfundenTileSlug, 'eigene'>, string>
> = {
  feuerwehr: STRINGS.book.geschichtenschrittPrototype.erfundenThemes.feuerwehr,
  weltraum: STRINGS.book.geschichtenschrittPrototype.erfundenThemes.weltraum,
  piraten: STRINGS.book.geschichtenschrittPrototype.erfundenThemes.piraten,
  superhelden: STRINGS.book.geschichtenschrittPrototype.erfundenThemes.superhelden,
  polizei: STRINGS.book.geschichtenschrittPrototype.erfundenThemes.polizei,
  zauberwald: STRINGS.book.geschichtenschrittPrototype.erfundenThemes.zauberwald,
  dino: STRINGS.book.geschichtenschrittPrototype.erfundenThemes.dino,
  unterwasser: STRINGS.book.geschichtenschrittPrototype.erfundenThemes.unterwasser,
};

/**
 * Capitalise the first character for display. Round-3b display rule
 * (Sebastian 2026-10-08): "finn" → "Finn" in the adventure-question
 * heading and in the prefill sentence. Stored cast names are not
 * mutated — only the strings this module renders out.
 */
export function capitalizeFirst(s: string): string {
  if (s.length === 0) return s;
  return s[0]!.toUpperCase() + s.slice(1);
}

export function joinNames(rawNames: readonly string[]): string {
  const names = rawNames
    .map((n) => capitalizeFirst(n.trim()))
    .filter((n) => n.length > 0);
  if (names.length === 0) return '';
  if (names.length === 1) return names[0]!;
  if (names.length === 2) return `${names[0]} und ${names[1]}`;
  const head = names.slice(0, -1).join(', ');
  const tail = names[names.length - 1]!;
  return `${head} und ${tail}`;
}

export function joinCastNames(cast: readonly ErfundenCastEntry[]): string {
  return joinNames(cast.map((c) => c.name));
}

/**
 * Returns the field-prefill text for an erfunden tile. Each non-eigene
 * tile pulls its own approved sentence from
 * `STRINGS.book.geschichtenschrittPrototype.erfundenSentenceTemplates`
 * (Sebastian 2026-10-09 10:15 — eight theme-specific sentences, each
 * with a `{Namen}` placeholder). The 'eigene' tile deliberately
 * returns '' (customer writes free text in an empty field).
 */
export function erfundenSentenceForTile(
  tile: ErfundenTileSlug,
  cast: readonly ErfundenCastEntry[],
): string {
  if (tile === 'eigene') return '';
  const names = joinCastNames(cast);
  return STRINGS.book.geschichtenschrittPrototype.erfundenSentenceTemplates[tile].replace(
    '{Namen}',
    names,
  );
}

/** Field heading on erfunden step 3. */
export function erfundenFieldHeading(tile: ErfundenTileSlug): string {
  if (tile === 'eigene') return STRINGS.book.geschichtenschrittPrototype.eigeneFieldHeading;
  return STRINGS.book.geschichtenschrittPrototype.erfundenFieldHeadingTemplate.replace(
    '{Theme}',
    TILE_THEMES[tile],
  );
}

/** Dynamic question heading on erfunden step 2 (tiles). */
export function adventureQuestionForCast(cast: readonly ErfundenCastEntry[]): string {
  const names = cast
    .map((c) => capitalizeFirst(c.name.trim()))
    .filter((n) => n.length > 0);
  const s = STRINGS.book.geschichtenschrittPrototype;
  if (names.length === 0) return s.adventureQuestionMany;
  if (names.length === 1) return s.adventureQuestionOne.replace('{name}', names[0]!);
  if (names.length <= 3) {
    return s.adventureQuestionFew.replace('{names}', joinNames(names));
  }
  return s.adventureQuestionMany;
}
